<template>
    <div class="flex items-center justify-between">
      <span class="text-xl font-semibold text-gray-900">{{ $gettext("Rapports") }}</span>
      <button
        type="button"
        @click="$emit('build')"
        class="rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold shadow-sm hover:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
      >
        {{ $gettext("Générer") }}
      </button>
    </div>

    <div class="mt-8 flow-root max-w-full">
      <div class="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8 max-w-[900px]">
          <table class="relative w-full divide-y divide-gray-300 table-auto">
            <thead>
              <tr>
                <th class="py-3.5 px-4 text-left text-sm font-semibold text-gray-900">{{ $gettext("ID") }}</th>
                <th class="py-3.5 px-4 text-left text-sm font-semibold text-gray-900">{{ $gettext("Créé le") }}</th>
                <th class="py-3.5 px-4 text-left text-sm font-semibold text-gray-900">{{ $gettext("Actions") }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-for="report in reports" :key="report.id">
                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                  {{ report.id.slice(-6) }}
                </td>
                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">{{ parseDate(report.attributes.createdAt) }}</td>
                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900 flex gap-2">
                  <button @click="openReport(report)" class="text-indigo-600 hover:scale-110 transition-transform duration-200">
                    <MagnifyingGlassIcon class="inline h-5 w-5 mr-1" />
                  </button>
                  <button @click="$emit('delete', report)" class="text-red-600 hover:scale-110 transition-transform duration-200">
                    <TrashIcon class="inline h-5 w-5 mr-1" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="!reports || reports.length === 0" class="text-center text-gray-500 text-sm py-6">
            {{ $gettext("Aucun rapport disponible.") }}
          </div>
        </div>
      </div>
    </div>

    <ReportModal
      v-if="selectedReport"
      :open="modalOpen"
      :report-type="reportType"
      :file-name="getFileName(selectedReport, reportType)"
      @close="closeReport"
    >
      <component
        :is="reportComponents[reportType]"
        :report="selectedReport"
      />
    </ReportModal>
</template>

<script setup>
import { ref } from "vue";
import ReportModal from "./ReportModal.vue";
import PhysicalPersonReport from "./PhysicalPersonReport.vue";
import CompanyReport from "./CompanyReport.vue";
import RentalListingAgreementReport from "./RentalListingAgreementReport.vue";
import SaleListingAgreementReport from "./SaleListingAgreementReport.vue";
import { useDate } from "@/utils/stores/date";
import { MagnifyingGlassIcon, TrashIcon } from "@heroicons/vue/24/outline";

const { parseDate } = useDate();

defineProps({
  reports: {
    type: Array,
    required: true,
    default: () => []
  },
  reportType: {
    type: String,
    required: true
  }
});

defineEmits(["build", "delete"]);

const modalOpen = ref(false);
const selectedReport = ref(null);

const reportComponents = {
  physicalPerson: PhysicalPersonReport,
  company: CompanyReport,
  rentalListingAgreement: RentalListingAgreementReport,
  saleListingAgreement: SaleListingAgreementReport
};

const sanitizeFileName = (filename) => {
  // Normalize and remove accents
  let sanitized = filename
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Remove or replace special characters that are problematic in filenames
    .replace(/[/\\:*?"<>|]/g, '-')
    // Replace multiple spaces with a single underscore
    .replace(/\s+/g, '_')
    // Remove leading/trailing underscores
    .replace(/^_+|_+$/g, '');
  return sanitized;
};

const getFileName = (report, reportType) => {
  const data = report?.attributes?.data;
  let name = '';

  switch (reportType) {
    case 'physicalPerson':
      if (data?.attributes?.firstName && data?.attributes?.lastName) {
        name = `${data.attributes.firstName} ${data.attributes.lastName}`;
      } else {
        name = 'physicalPerson';
      }
      break;
    case 'company':
      if (data?.attributes?.name) {
        name = data.attributes.name;
      } else {
        name = 'company';
      }
      break;
    case 'rentalListingAgreement':
      // First try to get fileId/reference
      if (data?.attributes?.fileId) {
        name = data.attributes.fileId;
      } else {
        // Then try to get landlord names
        const rentalLandlords = data?.relationships?.rentalListingAgreementLandlords?.data;
        if (rentalLandlords && rentalLandlords.length > 0) {
          name = rentalLandlords.map(l => l?.attributes?.name).filter(Boolean).join(' - ');
        }
      }
      break;
    case 'saleListingAgreement':
      // First try to get fileId/reference
      if (data?.attributes?.fileId) {
        name = data.attributes.fileId;
      } else {
        // Then try to get seller names
        const saleSellers = data?.relationships?.saleListingAgreementSellers?.data;
        if (saleSellers && saleSellers.length > 0) {
          name = saleSellers.map(s => s?.attributes?.name).filter(Boolean).join(' - ');
        }
      }
      break;
  }

  if (!name) {
    name = reportType;
  }

  const sanitized = sanitizeFileName(name);
  return `${sanitized}.pdf`;
};

const openReport = (report) => {
  selectedReport.value = report;
  modalOpen.value = true;
};

const closeReport = () => {
  modalOpen.value = false;
  selectedReport.value = null;
};
</script>
